/**
 * Advanced → Website content — content collections: a page per record of a
 * transaction on a published site (a blog, departments, products, jobs …).
 *
 * Authoring only (platform host). See backend `routers/content_collections.py`.
 */

import api from './client'

export interface ContentPreset {
  id: string
  label: string
  description: string
  singular: string
  plural: string
  route: string
  structured: string
  feed: boolean
}

export interface ContentTaxonomy {
  id: string
  column: string
  label: string
  multi?: boolean
}

export interface ContentProfile {
  enabled?: boolean
  kind?: string
  label?: { singular?: string; plural?: string }
  roles?: Record<string, string>
  visibility?: { status_column?: string; live_values?: string[]; schedule_column?: string }
  seo?: Record<string, string>
  structured_data?: { type?: string; organization?: string; logo?: string }
  taxonomies?: ContentTaxonomy[]
  feed?: boolean
  routes?: { index?: string; item?: string }
  public_columns?: string[]
}

export interface ContentObject {
  obj_name: string
  title: string
  content: boolean
  kind?: string | null
  label?: { singular: string; plural: string } | null
  /** Reader comments (moderated in the Run view) — null when never switched on. */
  comments?: { enabled: boolean; object: string; moderation: boolean } | null
  /** New-record e-mails to subscribers. */
  newsletter?: { enabled: boolean; object: string } | null
  errors: string[]
  warnings: string[]
}

export interface ContentSite {
  slug: string
  title: string
  node_id: string
  settings: Record<string, unknown>
  auto_publish_content: boolean
}

export interface ColumnInfo { name: string; heading?: string; col_type?: string }

export async function listContentPresets(): Promise<{
  presets: ContentPreset[]; roles: Record<string, string>; structured_types: string[]
}> {
  return (await api.get('/content-collections/presets')).data
}

export async function getContentOverview(project: string): Promise<{
  objects: ContentObject[]; sites: ContentSite[]
}> {
  return (await api.get(`/content-collections/project/${encodeURIComponent(project)}`)).data
}

/** The application's websites only — a cheap "does this app have a website?" */
export async function listContentSites(project: string): Promise<{ sites: ContentSite[] }> {
  return (await api.get(`/content-collections/project/${encodeURIComponent(project)}/sites`)).data
}

export async function getContentObject(project: string, objName: string): Promise<{
  obj_name: string
  profile: ContentProfile | null
  normalized: ContentProfile | null
  errors: string[]
  warnings: string[]
  columns: ColumnInfo[]
}> {
  return (await api.get(
    `/content-collections/project/${encodeURIComponent(project)}/object/${encodeURIComponent(objName)}`,
  )).data
}

export async function validateContentProfile(project: string, objName: string, profile: ContentProfile) {
  return (await api.post(`/content-collections/project/${encodeURIComponent(project)}/validate`,
    { obj_name: objName, profile })).data as { ok: boolean; errors: string[]; warnings: string[] }
}

export interface CreateCollectionBody {
  kind: string
  obj_name: string
  singular?: string
  plural?: string
  route?: string
  site_slug?: string | null
  grant_role?: string | null
  organization?: string
  intro?: string
}

export async function createCollection(project: string, body: CreateCollectionBody) {
  return (await api.post(`/content-collections/project/${encodeURIComponent(project)}/create`, body))
    .data as { obj_name: string; pages: string[]; route: string; site_slug?: string | null }
}

export async function enableCollection(project: string, body: {
  obj_name: string; profile: ContentProfile; site_slug?: string | null; create_pages?: boolean
}) {
  return (await api.post(`/content-collections/project/${encodeURIComponent(project)}/enable`, body))
    .data as { obj_name: string; profile: ContentProfile; warnings: string[]; pages: string[] }
}

export async function disableCollection(project: string, objName: string) {
  return (await api.put(
    `/content-collections/project/${encodeURIComponent(project)}/object/${encodeURIComponent(objName)}/disable`,
  )).data
}

export async function saveSiteSettings(project: string, site: string, settings: Record<string, unknown>,
  autoPublish?: boolean) {
  return (await api.put(
    `/content-collections/project/${encodeURIComponent(project)}/sites/${encodeURIComponent(site)}/settings`,
    { settings, auto_publish_content: autoPublish },
  )).data as { settings: Record<string, unknown> }
}

export async function refreshSiteContent(project: string, site: string) {
  return (await api.post(
    `/content-collections/project/${encodeURIComponent(project)}/sites/${encodeURIComponent(site)}/refresh`,
  )).data
}

/** Server `detail` (string) when present, else the fallback. */
export function contentErr(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

export async function setCollectionComments(project: string, objName: string, enabled: boolean,
  moderation = true) {
  return (await api.post(
    `/content-collections/project/${encodeURIComponent(project)}/collections/${encodeURIComponent(objName)}/comments`,
    { enabled, moderation },
  )).data as { comments: ContentObject['comments']; object?: string }
}

export async function setCollectionNewsletter(project: string, objName: string, enabled: boolean) {
  return (await api.post(
    `/content-collections/project/${encodeURIComponent(project)}/collections/${encodeURIComponent(objName)}/newsletter`,
    { enabled },
  )).data as { newsletter: ContentObject['newsletter']; object?: string }
}
